/**
 * Outcome mapping for the Salesforce account link. Pure: `messageKey` is an
 * i18n key name, never display text.
 *
 * `SalesforceAccountLinkResult.outcome` drives the UI, not the HTTP status
 * alone — a `200` can still be `unchanged`, and both 409s are dialog states.
 *
 * The only place in the feature allowed to read `ApiError.body`. The generated
 * error unions (`{status: 400, payload}`) are dead at runtime: the fetcher
 * throws `ApiError`, which names the body `body`, or `NetworkError`. Confining
 * that cast to `parseLinkResult` keeps the discrepancy in one place.
 */
import { ApiError } from '@/api/ApiError'
import type { SalesforceAccountLinkResult } from '@/generated/http-clients/v3ApiSchemas'

/**
 * 15- or 18-character alphanumeric, mirroring the contract's `@pattern` on
 * `SalesforceAccountLinkRequest.salesforceAccountId`.
 *
 * Applied only to an id the operator *creates*. The compare-and-swap token is
 * deliberately never validated: it is an exact-match target, so a malformed
 * value simply cannot match, while rejecting it would make an out-of-pattern
 * stored value permanently unconfirmable.
 */
export const SALESFORCE_ACCOUNT_ID_PATTERN =
  /^[A-Za-z0-9]{15}([A-Za-z0-9]{3})?$/

/**
 * Does not trim. Callers trim first: trimming in here would make
 * `" 0011t00000XyZaBAAV "` report as valid while the value actually submitted
 * still carried the spaces, and the write would fail with no visible cause.
 */
export function isValidSalesforceAccountId(value: string): boolean {
  return SALESFORCE_ACCOUNT_ID_PATTERN.test(value)
}

/**
 * Statuses the dialog renders in place instead of toasting: 400 is a field
 * error, both 409s are the conflict panel, 503 disables submit and 502 is an
 * in-dialog error. Everything else closes the dialog behind a toast.
 */
export const SALESFORCE_INLINE_STATUSES = [400, 409, 502, 503]

export type SalesforceOutcomeKind =
  | 'success'
  | 'neutral'
  | 'conflict'
  | 'invalid'
  | 'notFound'
  | 'disabled'
  | 'failed'

/** `messageKey` of the default branch: the answer was not recognised. */
const UNRECOGNISED_MESSAGE_KEY = 'unknown'

export type SalesforceOutcomeDecision = {
  /** The raw outcome token, echoed so callers can log or assert on it. */
  outcome: string
  kind: SalesforceOutcomeKind
  closesDialog: boolean
  invalidates: boolean
  toast: 'success' | 'neutral' | 'error' | null
  /** True only for `account_service_disabled`: retrying cannot succeed. */
  blocksSubmit: boolean
  /** i18n key name under `salesforceLink.outcome`, never display text. */
  messageKey: string
}

/**
 * Maps one outcome token to everything the UI has to decide.
 *
 * The default branch is load-bearing: `outcome` is a plain `string` in the
 * contract precisely so an unrecognised upstream token passes through
 * deserialization instead of failing it. Such a token means "the link may or
 * may not have changed", so it neither closes the dialog nor claims success.
 */
export function decideOutcome(outcome: string): SalesforceOutcomeDecision {
  switch (outcome) {
    case 'linked':
      return {
        outcome,
        kind: 'success',
        closesDialog: true,
        invalidates: true,
        toast: 'success',
        blocksSubmit: false,
        messageKey: 'linked',
      }
    case 'rebound':
      return {
        outcome,
        kind: 'success',
        closesDialog: true,
        invalidates: true,
        toast: 'success',
        blocksSubmit: false,
        messageKey: 'rebound',
      }
    case 'unlinked':
      return {
        outcome,
        kind: 'success',
        closesDialog: true,
        invalidates: true,
        toast: 'success',
        blocksSubmit: false,
        messageKey: 'unlinked',
      }
    // A 200 that wrote nothing, because the stored value already matched.
    // Reporting it as a success would claim the operator's edit landed.
    case 'unchanged':
      return {
        outcome,
        kind: 'neutral',
        closesDialog: true,
        invalidates: true,
        toast: 'neutral',
        blocksSubmit: false,
        messageKey: 'unchanged',
      }
    case 'confirmation_required':
      return {
        outcome,
        kind: 'conflict',
        closesDialog: false,
        invalidates: false,
        toast: null,
        blocksSubmit: false,
        messageKey: 'confirmationRequired',
      }
    case 'conflict':
      return {
        outcome,
        kind: 'conflict',
        closesDialog: false,
        invalidates: false,
        toast: null,
        blocksSubmit: false,
        messageKey: 'conflict',
      }
    case 'invalid':
      return {
        outcome,
        kind: 'invalid',
        closesDialog: false,
        invalidates: false,
        toast: null,
        blocksSubmit: false,
        messageKey: 'invalid',
      }
    // Nothing the operator can fix in the dialog: the org itself is gone.
    case 'not_found':
      return {
        outcome,
        kind: 'notFound',
        closesDialog: true,
        invalidates: true,
        toast: 'error',
        blocksSubmit: false,
        messageKey: 'notFound',
      }
    // The only outcome that disables submit: the account-service client is off
    // in this environment, so re-pressing the button cannot succeed.
    case 'account_service_disabled':
      return {
        outcome,
        kind: 'disabled',
        closesDialog: false,
        invalidates: false,
        toast: null,
        blocksSubmit: true,
        messageKey: 'serviceDisabled',
      }
    case 'error':
      return {
        outcome,
        kind: 'failed',
        closesDialog: false,
        invalidates: false,
        toast: null,
        blocksSubmit: false,
        messageKey: 'error',
      }
    // Indeterminate, not merely unhandled: the write may or may not have
    // landed. Hence invalidating (the cached read can no longer be trusted)
    // and toasting (silence would read as "nothing happened") while refusing
    // to close the dialog.
    default:
      return {
        outcome,
        kind: 'failed',
        closesDialog: false,
        invalidates: true,
        toast: 'error',
        blocksSubmit: false,
        messageKey: UNRECOGNISED_MESSAGE_KEY,
      }
  }
}

/**
 * True when the service answered with a token this screen does not recognise.
 * The caller must re-read before offering another submission.
 */
export function isUnrecognisedOutcome(
  decision: SalesforceOutcomeDecision,
): boolean {
  return decision.messageKey === UNRECOGNISED_MESSAGE_KEY
}

/**
 * The `salesforceLink.outcome.*` key a *toast* may use for one decision.
 *
 * `confirmationRequired` and `conflict` deliberately have no entry under
 * `outcome.*` — they are conflict-panel headings, not toast copy — so a
 * conflict arriving on a status the dialog cannot render in place would
 * otherwise put a raw i18n key in a toast.
 */
export function toastMessageKey(decision: SalesforceOutcomeDecision): string {
  return decision.kind === 'conflict'
    ? UNRECOGNISED_MESSAGE_KEY
    : decision.messageKey
}

/**
 * What a failed link/clear call actually carries, once the runtime lie in the
 * generated error types is stripped away.
 *
 * `status` is `0` for anything that never reached a response, and `0` is in no
 * inline status list, so such a failure toasts and closes rather than silently
 * rendering an empty conflict panel.
 */
export type SalesforceErrorEnvelope = {
  status: number
  result?: SalesforceAccountLinkResult
  detail?: string
}

/**
 * The compare-and-swap token a `409` echoed back, reduced to "there is one" or
 * "there is not".
 *
 * The generated type says `previousSalesforceAccountId?: string`, but the
 * contract refuses a token supplied for an organisation that is actually
 * unlinked **with a null `previousSalesforceAccountId`** — so `null` arrives on
 * exactly the path where inventing a token does the most damage, and
 * `null !== undefined`. An empty string is the same answer by another route.
 * All three collapse to `undefined` here so no caller has to remember that
 * the type is lying.
 */
export function storedAccountIdOf(
  value: string | null | undefined,
): string | undefined {
  return value === null || value === undefined || value === ''
    ? undefined
    : value
}

/**
 * The outcome token to assume when a failure carried none the table knows. A
 * `503` with an empty body still has to disable submit, and a `400` with no
 * token is still a field error; without this, `decideOutcome('')` would report
 * `blocksSubmit: false` and re-arm a button that cannot succeed.
 */
const STATUS_FALLBACK_OUTCOMES: Record<number, string> = {
  400: 'invalid',
  409: 'conflict',
  502: 'error',
  503: 'account_service_disabled',
}

/**
 * `decideOutcome` for a *failed* write: the body's own token wins, and the HTTP
 * status only fills in when the body carried nothing recognisable. Lets the
 * forms read `kind` and `blocksSubmit` off the table instead of hard-coding
 * statuses.
 */
export function decideErrorOutcome(
  envelope: SalesforceErrorEnvelope,
): SalesforceOutcomeDecision {
  const decision = decideOutcome(envelope.result?.outcome ?? '')
  if (!isUnrecognisedOutcome(decision)) {
    return decision
  }
  const fallback = STATUS_FALLBACK_OUTCOMES[envelope.status]
  return fallback === undefined ? decision : decideOutcome(fallback)
}

/**
 * Narrows a thrown value to the envelope above.
 *
 * The cast on `.body` is deliberate: `ApiError.body` is `any` because error
 * bodies vary per endpoint, and both write endpoints declare
 * `SalesforceAccountLinkResult` for every failure status. The `typeof` guard
 * is what keeps a non-JSON body — a raw string, as `ApiError.fromResponse`
 * produces whenever the gateway answers HTML — from being cast.
 */
export function parseLinkResult(error: unknown): SalesforceErrorEnvelope {
  if (error instanceof ApiError) {
    const body: unknown = error.body
    const result =
      body !== null && typeof body === 'object'
        ? (body as SalesforceAccountLinkResult)
        : undefined
    return {
      status: error.status,
      result,
      detail: result?.detail,
    }
  }
  // `NetworkError` (the only other thing the fetcher throws) never got a
  // response, so it has no status and no result.
  return { status: 0 }
}
